/* eslint @typescript-eslint/strict-boolean-expressions: 0 */
import {
  find,
  includes,
  isEmpty,
  isEqual,
  isNil,
  keys,
  map,
  reduce,
  size,
  split,
  template
} from 'lodash/fp'
import React from 'react'
import {
  Form,
  type Identifier,
  type RaRecord,
  required,
  ShowBase,
  SimpleShowLayout,
  TextField,
  TextInput,
  useTranslate
} from 'react-admin'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material'
import log from 'loglevel'
import { zodResolver } from '@hookform/resolvers/zod'
import { useFormContext, useFormState } from 'react-hook-form'
import csv from 'csvtojson'
import { detect } from 'encoding-japanese'
import { MultipleSpotSelect } from 'components/SpotSelect'
import { hasConfig, type ConfigProps } from 'components/Dashboard'
import { DateInput, FileInput, useNotify } from 'components/Util'
import { sourceSchema } from './schema'
import { BIChart, CompareChart } from 'components/BI'
import { format, isAfter, parseISO, subDays } from 'date-fns/fp'
import { json2xlsx } from '@mzc-watchover/json2xlsx'
import * as xlsx from 'xlsx'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const SourceFormContent: React.FC<
  ConfigProps & {
    setData: (data: any) => void
    target: string | null | undefined
    selected: RaRecord[] | undefined
  }
> = ({ setData, target, config, selected }) => {
  const { name: nameKey, code: codeKey } = config.Mappings
  const notify = useNotify()
  const translate = useTranslate()
  const [open, setOpen] = React.useState(false)
  const { errors, isValid } = useFormState()
  const { getValues } = useFormContext()
  const values = getValues()
  const dialog = React.useMemo(() => {
    const handleClose = (): void => {
      setOpen(false)
    }
    const onClickClose = (): void => {
      setOpen(false)
    }
    const onClickSet = (): void => {
      const readCsv = async (file: File): Promise<any[]> => {
        const buf = await file.arrayBuffer()
        return detect(new Uint8Array(buf)) === 'SJIS'
          ? await csv().fromString(new TextDecoder('shift-jis').decode(buf))
          : await csv().fromString(await file.text())
      }
      const readXlsx = async (file: File): Promise<any[]> => {
        const buf = await file.arrayBuffer()
        const data = xlsx.read(buf)
        const name = data.SheetNames[0]
        return isNil(name) || isNil(data.Sheets[name])
          ? []
          : xlsx.utils.sheet_to_json(data.Sheets[name])
      }
      const readFile = async (): Promise<any> => {
        const { file } = getValues()
        const { type } = file
        if (type === 'text/csv') {
          return await readCsv(file)
        } else {
          return await readXlsx(file)
        }
      }
      setOpen(false)
      readFile()
        .then((data) => {
          // const { sourceKey, targetKey, dateKey } = getValues()
          // setData({ data, sourceKey, targetKey, dateKey })
          const { sourceKey } = getValues()
          const targetKey = `${nameKey as string}名`
          setData({ data, sourceKey, targetKey })
        })
        .catch(log.error)
    }
    return (
      <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
        <DialogTitle>
          {/* {config.dialogs.select.title ??
            translate(`resources.compare.dialogs.select.title`)} */}
          {translate(`resources.compare.dialogs.select.title`)}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexDirection: 'row'
            }}>
            <FileInput
              source="file"
              placeholder={translate('resources.compare.fileSelect')}
              inputProps={{
                accept:
                  'text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              }}
              variant="outlined"
              validate={required()}
              fullWidth
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexDirection: 'row',
              gap: 4
            }}>
            <TextInput
              source="sourceKey"
              label="resources.compare.fields.sourceKey.label"
              helperText={
                <>
                  {map((item: string) => (
                    <Typography variant="caption" key={item}>
                      {item}
                      <br />
                    </Typography>
                  ))(
                    split('\n')(
                      translate('resources.compare.fields.sourceKey.helperText')
                    )
                  )}
                </>
              }
              sx={{ mt: '1em' }}
              fullWidth
              validate={required()}
              defaultValue={isNil(process.env['INPUT']) ? null : '売上'}
            />
            {/* <SelectInput
                  source="targetKey"
                  label="resources.compare.fields.targetKey.label"
                  helperText={
                    <>
                      {map((item: string) => (
                        <Typography variant="caption">
                          {item}
                          <br />
                        </Typography>
                      ))(
                        split('\n')(
                          translate(
                            'resources.compare.fields.targetKey.helperText'
                          )
                        )
                      )}
                    </>
                  }
                  choices={map((key: string) => ({ id: key, name: key }))(
                    compact(config.targetKeys)
                  )}
                  sx={{ width: 200 }}
                  validate={required()}
                  defaultValue={isNil(process.env['INPUT']) ? null : '店舗'}
                />
                <SelectInput
                  source="dateKey"
                  label="resources.compare.fields.dateKey.label"
                  helperText={
                    <>
                      {map((item: string) => (
                        <Typography variant="caption">
                          {item}
                          <br />
                        </Typography>
                      ))(
                        split('\n')(
                          translate(
                            'resources.compare.fields.dateKey.helperText'
                          )
                        )
                      )}
                    </>
                  }
                  choices={map((key: string) => ({ id: key, name: key }))(
                    config.dateKeys
                  )}
                  sx={{ width: 200 }}
                  validate={required()}
                  defaultValue={
                    isNil(process.env['INPUT']) ? null : '2024年1月'
                  }
                /> */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={onClickSet}
            disabled={
              !isEmpty(errors) || !isValid || isNil(getValues()['file'])
            }>
            {translate('resources.compare.buttons.set')}
          </Button>
          <Button variant="outlined" onClick={onClickClose}>
            {translate('resources.compare.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }, [open, errors, isValid, values])
  const onClickInput = (): void => {
    setOpen(true)
  }
  const onClickFormat = (): void => {
    if (isNil(target)) return
    notify('downloading', { autoHideDuration: null })
    const data = isNil(selected)
      ? [
          {
            [`${codeKey as string}`]: '',
            [`${nameKey as string}名`]: '',
            [`${format('yyyy年MM月')(parseISO(target))}変数データ`]: ''
          }
        ]
      : map((item: RaRecord) => ({
          [`${codeKey as string}`]: item[codeKey],
          [`${nameKey as string}名`]: item['name'],
          [`${format('yyyy年MM月')(parseISO(target))}変数データ`]: ''
        }))(selected)
    json2xlsx(data, undefined, {})
      .then((data) => {
        notify('downloading', { autoHideDuration: 0 })
        if (!isNil(data)) {
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const url = URL.createObjectURL(blob)
          const download = document.createElement('a')
          download.href = url
          download.download = `変数データフォーマット.xlsx`
          download.click()
          window.URL.revokeObjectURL(url)
        } else {
          notify('no data')
        }
      })
      .catch(log.error)
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 2
          // ml: '1em',
          // p: '0.5em'
        }}>
        <Button
          variant="contained"
          onClick={onClickFormat}
          disabled={isNil(target)}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {map((text: string) => (
              <Typography variant="caption" sx={{ m: '-0.3em' }} key={text}>
                {text}
              </Typography>
            ))(split('\n')(translate('resources.compare.buttons.format')))}
          </Box>
        </Button>
        <Button variant="contained" onClick={onClickInput}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {map((text: string) => (
              <Typography variant="caption" sx={{ m: '-0.3em' }} key={text}>
                {text}
              </Typography>
            ))(split('\n')(translate('resources.compare.buttons.input')))}
          </Box>
        </Button>
      </Box>
      {dialog}
    </>
  )
}
const CompareShow: React.FC<
  {
    selected: RaRecord[]
    date: string
    compare: any
    labels: Array<{ label: string; color: string }>
  } & ConfigProps
> = ({ config, selected, date, compare, labels }) => {
  const translate = useTranslate()
  const [targets, setTargets] = React.useState<Identifier[]>([])
  const [items, setItems] = React.useState<Identifier[]>([])
  const [open, setOpen] = React.useState(false)
  const [visible, setVisible] = React.useState(false)
  const onClick = (item: { x: number; y: number; id: Identifier }): void => {
    const { id } = item
    if (!includes(id)(targets)) {
      if (size(targets) < 2) {
        const values = reduce(
          (sum: Identifier[], id: Identifier) => [...sum, id],
          []
        )(targets)
        values.push(id)
        setTargets(values)
      } else {
        setTargets([targets[1] as Identifier, id])
      }
    }
  }
  const onClickCompare = (): void => {
    setOpen(true)
  }
  const handleClose = (): void => {
    setOpen(false)
  }
  const dialog = React.useMemo(() => {
    const labelKey = hasConfig(config, 'compareChart.labelKey')
      ? config.compareChart.labelKey
      : 'name'
    const { spins, labels } = reduce(
      (
        result: { spins: string[]; labels: string[] },
        item: { spin: string; label: string } | undefined
      ) => {
        if (!isNil(item)) {
          result.spins.push(item.spin)
          result.labels.push(item.label)
        }
        return result
      },
      { spins: [], labels: [] }
    )(
      // @ts-expect-error ts(2339)
      map.convert({ cap: false })((id: Identifier, index: number) => {
        const user = find((item: RaRecord) => item.id === id)(selected)
        return !isNil(user)
          ? {
              spin: user['spin'],
              label: hasConfig(config, `compareChart.label`)
                ? template(config.compareChart.label)({
                    // label: `${config.compareChart.prefix[index] as string}: ${
                    //   user[labelKey] as string
                    // }`
                    label: user[labelKey] as string
                  })
                : user[labelKey]
            }
          : undefined
      })(targets)
    )
    if (size(spins) !== size(targets)) return
    return (
      <Dialog onClose={handleClose} open={open} maxWidth="xl" fullWidth>
        {/* <DialogTitle>
          {config.dialogs.compare.title ??
            translate(`resources.compare.dialogs.compare.title`)}
        </DialogTitle> */}
        <DialogContent>
          <CompareChart
            config={config}
            date={date}
            targets={spins}
            labels={labels}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {translate('resources.compare.buttons.close')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }, [open])
  React.useEffect(() => {
    const ids = map((item: RaRecord) => item.id)(selected)
    if (!isEqual(ids)(items)) {
      setItems(ids)
      setTargets([])
    }
  }, [selected])
  return (
    <>
      <Grid container spacing={2} sx={{ m: '0.5em' }}>
        <Grid size={8}>
          {!isEmpty(selected) && (
            <BIChart
              config={config}
              selected={selected}
              date={date}
              compare={compare}
              labels={labels}
              onClickItem={onClick}
              setVisible={setVisible}
            />
          )}
        </Grid>
        <Grid size={4}>
          {visible && (
            <Grid container spacing={2} sx={{ mt: '0.5em' }}>
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    flexDirection: 'row'
                  }}>
                  <Button
                    variant="contained"
                    onClick={onClickCompare}
                    disabled={size(targets) !== 2}>
                    {translate('resources.compare.buttons.compare')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
          {
            // @ts-expect-error ts(2339)
            map.convert({ cap: false })((id: Identifier, index: number) => (
              <Grid size={12} sx={{ mt: '0.5em' }}>
                <Card>
                  <Box sx={{ m: 0.3 }}>
                    <Typography variant="h6">
                      {translate(`resources.compare.show.${index}`)}
                    </Typography>
                    <ShowBase resource="mappings" id={id}>
                      <SimpleShowLayout>
                        {map((key: string) => {
                          return (
                            <TextField
                              source={key}
                              label={
                                config.fields[key].label ??
                                translate(`resources.mergedUsers.fields.${key}`)
                              }
                            />
                          )
                        })(keys(config.fields))}
                      </SimpleShowLayout>
                    </ShowBase>
                  </Box>
                </Card>
              </Grid>
            ))(targets)
          }
        </Grid>
      </Grid>
      {dialog}
    </>
  )
}

// const getFormat = (dateKey: string): string | undefined => {
//   const formats = [
//     { check: /^\d{1}月/, format: 'M月' },
//     { check: /^\d{2}月/, format: 'MM月' },
//     { check: /^\d{4}年\d{1}月/, format: 'yyyy年M月' },
//     { check: /^\d{4}年\d{2}月/, format: 'yyyy年MM月' },
//     { check: /^\d{2}年\d{1}月/, format: 'yy年M月' },
//     { check: /^\d{2}年\d{2}月/, format: 'yy年MM月' },
//     { check: /^\d{1}/, format: 'M' },
//     { check: /^\d{2}/, format: 'MM' },
//     { check: /^\d{6}/, format: 'yyyyMM' },
//     { check: /^\d{4}\/\d{1}/, format: 'yyyy/M' },
//     { check: /^\d{4}\/\d{2}/, format: 'yyyy/MM' },
//     { check: /^\d{4}-\d{1}/, format: 'yyyy-M' },
//     { check: /^\d{4}-\d{2}/, format: 'yyyy-MM' }
//   ]
//   const item = find((item: { check: RegExp; format: string }) =>
//     item.check.test(dateKey)
//   )(formats)
//   return !isNil(item) ? item.format : undefined
// }

export const Compare: React.FC<ConfigProps & { permissions: string[] }> = ({
  config,
  permissions
}) => {
  const { Compare: configCompare, Limits: configLimits } = config
  const translate = useTranslate()
  const [deleted, setDeleted] = React.useState(false)
  const [selected, setSelected] = React.useState<RaRecord[]>([])
  const [target, setTarget] = React.useState<string | null>(
    isNil(process.env['INPUT']) ? null : '2024-12-01'
  )
  const [sourceData, setSourceData] = React.useState<any | null>(
    isNil(process.env['INPUT'])
      ? null
      : { sourceKey: '売上', targetKey: '店舗', dateKey: '2024年1月' }
  )
  const { datasets } = configCompare.biChart
  const [categoryLabels, setCategoryLabels] = React.useState<
    Array<{ label: string; color: string }>
  >(map((item: { label: string; color: string }) => item)(datasets))
  const [show, setShow] = React.useState(false)
  // const notify = useNotify()
  const reduceSourceData = (): void => {
    // const { data, sourceKey, targetKey, dateKey } = sourceData
    const { data, sourceKey, targetKey } = sourceData
    // const dateFormat = getFormat(dateKey)
    // if (isNil(dateFormat)) {
    //   notify('illegal dateKey')
    //   return
    // }
    // const dateValue = format(dateFormat)(parseISO(target as string))
    // const items = map((item: Record<string, any>) => {
    //   return {
    //     ...item,
    //     [targetKey]: item[targetKey],
    //     [target as string]: item[dateValue]
    //   }
    // })(data)
    // setSourceData({ data: items, sourceKey, targetKey, dateKey })
    const items = map((item: Record<string, any>) => {
      return {
        ...item,
        [target as string]:
          item[`${format('yyyy年MM月')(parseISO(target as string))}変数データ`]
      }
    })(data)
    setSourceData({ data: items, sourceKey, targetKey })
  }
  const onClickExecute = (): void => {
    reduceSourceData()
    setShow(true)
  }
  React.useEffect(() => {
    if (isNil(sourceData)) return
    reduceSourceData()
  }, [target])
  // if (
  //   !includes('manager')(permissions) &&
  //   !includes('highManager')(permissions)
  // ) {
  //   return null
  // }
  const { start } = configLimits ?? { start: undefined }
  return (
    <>
      <Form resolver={zodResolver(sourceSchema)}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: window.innerWidth <= 900 ? 'column' : 'row',
            alignItems: window.innerWidth <= 900 ? 'flex-start' : 'center',
            bgcolor: '#F9DD87'
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              flexDirection: window.innerWidth < 1300 ? 'column' : 'row',
              gap: window.innerWidth <= 900 ? '0.5em' : '1em',
              pl: '0.5em',
              m: window.innerWidth <= 900 ? 0 : '0.5em'
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: window.innerWidth <= 900 ? 'column' : 'row',
                gap: window.innerWidth <= 900 ? '0.5em' : '1em'
              }}>
              <MultipleSpotSelect
                resource="mappings"
                selected={selected}
                setSelected={setSelected}
                deleted={deleted}
                setDeleted={setDeleted}
                config={config}
                withButton
                permissions={permissions}
              />
              <DateInput
                source="target"
                label={translate('resources.compare.buttons.target')}
                validate={required()}
                disableFuture
                views={['year', 'month']}
                // sx={window.innerWidth <= 900 ? {} : { ml: '1em' }}
                onChange={(_source: string, value: string): void => {
                  setTarget(value)
                }}
                defaultValue={isNil(process.env['INPUT']) ? null : '2024-12-01'}
                minDate={!isNil(start) ? parseISO(start) : undefined}
                maxDate={
                  isAfter(
                    parseISO(`${format('yyyy-MM-dd')(Date.now())}T15:00`)
                  )(Date.now())
                    ? subDays(1)(Date.now())
                    : subDays(2)(Date.now())
                }
                withControl
                controlLabel="日付選択"
              />
            </Box>
            <SourceFormContent
              config={config}
              setData={setSourceData}
              target={target}
              selected={selected}
            />
          </Box>
          <Button
            variant="contained"
            disabled={isNil(target) || isNil(sourceData) || isEmpty(selected)}
            sx={{ m: '0.5em', pl: '2em', pr: '2em' }}
            onClick={onClickExecute}>
            {translate('resources.compare.buttons.execute')}
          </Button>
        </Box>
        {show && (
          <CompareShow
            config={configCompare}
            selected={selected}
            date={target as string}
            compare={sourceData}
            labels={categoryLabels}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            p: '0.5em',
            mt: '1em',
            bgcolor: '#F9DD87'
          }}>
          {map((index: number) => (
            <TextInput
              key={`categoryLabel_${index}`}
              source={`categoryLabel_${index}`}
              label={translate(
                `resources.compare.fields.categories.labels.${index}`
              )}
              onChange={(event): void => {
                const labels = JSON.parse(JSON.stringify(categoryLabels))
                labels[index].label = event.target.value
                setCategoryLabels(labels)
              }}
              defaultValue={categoryLabels[index]?.label}
              fullWidth
              sx={{
                bgcolor: '#FEF7E3',
                height: window.innerWidth <= 900 ? '2.6em' : '3em'
              }}
            />
          ))([2, 3, 0, 1])}
        </Box>
      </Form>
      {/* {!isEmpty(selected) && (
        <Paper sx={{ ml: '0.5em', mt: '1em' }}>
          <Box sx={{ p: '0.5em' }}>
            <Typography variant="body1">
              {configCompare.selected ??
                translate('resources.compare.selected')}
            </Typography>
            {
              // @ts-expect-error ts(2339)
              map.convert({ cap: false })((item: RaRecord, index: number) =>
                size(selected) > 10 ? (
                  <Chip
                    key={`selected-${index}`}
                    label={item['name']}
                    sx={{ mr: '0.5em' }}
                  />
                ) : (
                  <Chip
                    key={item['name']}
                    onDelete={() => {
                      setDeleted(true)
                      setSelected(
                        filter((record: RaRecord) => record.id !== item.id)(
                          selected
                        )
                      )
                    }}
                    label={item['name']}
                    sx={{ mr: '0.5em' }}
                  />
                )
              )(size(selected) > 10 ? slice(0, 10)(selected) : selected)
            }
            {size(selected) > 10 && <Chip label="・・・" />}
          </Box>
        </Paper>
      )} */}
      {/* <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexDirection: 'row',
          m: 2
        }}>
        <Button
          variant="contained"
          onClick={onClickExecute}
          disabled={isNil(target) || isNil(sourceData) || isEmpty(selected)}>
          {translate('resources.compare.buttons.execute')}
        </Button>
      </Box> */}
    </>
  )
}
